import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input, Output, inject } from '@angular/core';
import type { UntypedFormControl } from '@angular/forms';
import { UntypedFormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { GradientObject, GradientText } from '../types/color-picker.type';
import { EColorPickerTypes } from '../enum/color-picker.enum';
import { defaultColor, defaultDeg } from '../constants/color-picker.const';
import { isHexColor } from '../helpers';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ColorPickerDirective } from 'ngx-color-picker';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpTransparentIconComponent } from '../../../../ui/sp-transparent-icon/src/sp-transparent-icon.component';
import { TranslateModule } from '@ngx-translate/core';
import { RgbaToHexPipe } from '../pipes/rgba-to-hex.pipe';

@Component({
    selector: 'sp-text-gradient',
    templateUrl: './sp-text-gradient.component.html',
    styleUrls: ['./sp-text-gradient.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ColorPickerDirective,
        ReactiveFormsModule,
        FormsModule,
        SpTransparentIconComponent,
        TranslateModule,
        RgbaToHexPipe,
    ],
})
export class SpTextGradientComponent implements OnInit, OnDestroy {
    private readonly fb = inject(UntypedFormBuilder);
    private readonly bsModalRef = inject(BsModalRef);

    @Input() text;

    @Output() public saveData: Subject<GradientText> = new Subject();
    @Output() public cancel: Subject<void> = new Subject();

    private readonly splitRegExp: RegExp = /,(?![^(]*\))(?![^"']*["'](?:[^"']*["'][^"']*["'])*[^"']*$)/gi;
    private readonly unsubscribe$ = new Subject<void>();

    public gradientValueIndex = 0;
    public formColor: UntypedFormControl;
    public formDeg: UntypedFormControl;
    public gradientObject: GradientObject = null;

    public gradientText: GradientText = null;

    public defaultGradientText: GradientText = {
        deg: '0',
        values: [
            { color: '#0075FF', position: '0%' },
            { color: '#1EED7D', position: '100%' },
        ],
    };

    public colorPickerType: EColorPickerTypes;
    public readonly EColorPickerTypes = EColorPickerTypes;
    store: any;

    constructor() {
        this.formColor = this.fb.control(defaultColor);
        this.formDeg = this.fb.control(defaultDeg);
    }

    public ngOnInit(): void {
        const computedStyle = window.getComputedStyle(this.text);

        const background = computedStyle.background;

        if (background.includes('gradient')) {
            this.gradientText = this.formatGradient(background);
        } else {
            this.gradientText = this.defaultGradientText;
        }

        this.formColor.patchValue(this.gradientText.values[0].color);
        this.formDeg.patchValue(this.gradientText.deg);

        this.formColor.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((color) => this.setColor(color));
        this.formDeg.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((deg) => this.setDeg(deg));
    }

    private formatGradient(value): GradientText {
        const gradientRegex = /linear-gradient\(([^,]+),\s*(.+)\)/;
        const match = value.match(gradientRegex);

        if (!match) {
            return null;
        }

        const angleOrDirection = match[1].trim();
        const stops = match[2];

        let deg;

        if (angleOrDirection.includes('deg')) {
            deg = angleOrDirection.replace('deg', '').trim();
        } else if (angleOrDirection.startsWith('to')) {
            const directionMap = {
                'to top': '0',
                'to right': '90',
                'to bottom': '180',
                'to left': '270',
            };
            deg = directionMap[angleOrDirection] || '0';
        } else {
            deg = '0';
        }

        const stopRegex = /(rgba?\([^)]*\)|#[a-fA-F0-9]{3,6}|[a-zA-Z]+)(?:\s+([\d.]+%)?)?/g;
        const values = [];
        let stopMatch;

        while ((stopMatch = stopRegex.exec(stops)) !== null) {
            const color = stopMatch[1].trim();
            const position = stopMatch[2] ? stopMatch[2].trim() : '0%';
            values.push({ color, position });
        }

        return {
            deg: deg,
            values: values,
        };
    }

    private setColor(value: string): void {
        this.gradientText.values[this.gradientValueIndex].color = value;
    }

    private setDeg(value: string): void {
        this.gradientText.deg = value;
    }

    public changeColorHandler(value: string): void {
        if (!isHexColor(value)) {
            return this.formColor.setErrors({ pattern: true });
        }

        return this.formColor.setValue(value);
    }

    public changeDegHandler(value: number): void {
        const strValue = value.toString();

        return this.formDeg.setValue(strValue);
    }

    public setGradientValueIndex(index: number): void {
        this.formColor.setValue(this.gradientText.values[index].color, { emitEvent: false });
        this.gradientValueIndex = index;
    }

    public getAlphaFromRgba(rgba: string) {
        const alpha = parseFloat(rgba.split(',')[3]);
        let alphaValue;

        if (isNaN(alpha)) {
            alphaValue = 100;
        } else {
            alphaValue = Math.round(+alpha * 100);
        }

        return alphaValue;
    }

    public get colorValue(): string {
        return this.formColor.value;
    }

    public get degValue(): string {
        return this.formDeg.value;
    }

    public set colorValue(value: string) {
        this.formColor.setValue(value);
    }

    public get gradientColor(): string {
        return this.gradientText.values[this.gradientValueIndex].color;
    }

    public getGradientBgStyle(gradient): string {
        const gradientCSS = `-webkit-linear-gradient(${gradient.deg}deg, ${gradient.values
            .map((value) => `${value.color} ${value.position}`)
            .join(', ')})`;

        return gradientCSS;
    }

    public onClose(): void {
        this.cancel.next();
        this.bsModalRef.hide();
    }

    public onConfirm(): void {
        this.saveData.next(this.gradientText);
        this.bsModalRef.hide();
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
